import {
    asBoolean,
    asNumber,
    asString,
    loadedDefinedMap,
    loadedDefinedMapWithDefault,
} from "common/components/component-helpers";
import { TextPosition, TilesPadding } from "common/components/image-types";
import type { ValueProducer } from "common/components/types";
import { ActionSource, asUI, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { panic } from "common/Support";
import { useForce } from "hooks/use-force";
import { useNetworkStatus } from "hooks/use-network-status";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import type { ListGroup } from "webapp/ui/smart/list/list-types";
import { pageMarginMedium } from "../lib/theme/theme";
import { styled } from "../styled-components";
import List from "../ui/smart/list/list";
import { makeListItems } from "./list-material-renderer";

function groupArr<T>(data: readonly T[], n: number): T[][] {
    const group: T[][] = [];
    for (let i = 0, j = 0; i < data.length; i++) {
        if (i >= n && i % n === 0) j++;
        group[j] = group[j] || [];
        group[j].push(data[i]);
    }
    return group;
}

const Container = styled.div<{ cols: number; gap: number; bottomMargin: number }>`
    display: grid;
    padding: 0 ${p => p.gap - p.theme.tileItem.padding}px ${p => p.bottomMargin - p.theme.tileItem.padding}px;

    grid-column-gap: ${p => p.gap - p.theme.tileItem.padding}px;

    grid-template-columns: repeat(${p => p.cols}, 1fr);
`;

export const VerticalTilesMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force, dataChanged] = useForce();
    const [
        [
            numColumns,
            padding,
            textPosition,
            list,
            itemUI,
            caption,
            emptyMessage,
            onSeeAllVP,
            isGroupedPV,
            allowWrapping,
        ],
        ,
        valueProducersChanged,
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const onLine = useNetworkStatus();
    const { onClick: onSeeAll } = useOnClickFromAction(onSeeAllVP, ActionSource.None, ctx, env);

    const fromData = dataChanged || valueProducersChanged;
    if (list === LOADING || itemUI === LOADING || numColumns === LOADING || padding === LOADING) {
        return env.renderProgress();
    }

    const cols = asNumber(numColumns);
    const allowWrappingValue = loadedDefinedMapWithDefault(allowWrapping, false, asBoolean);
    const textPositionValue = loadedDefinedMapWithDefault(textPosition, TextPosition.Below, asString) as TextPosition;

    let items: ValueProducer[][] | undefined;
    let groups: ListGroup<ValueProducer[]>[] | undefined;

    const listItems = makeListItems(list, isGroupedPV, force, ctx, env, onLine);
    if (listItems === undefined) return null;
    if (listItems.items !== undefined) {
        items = groupArr(listItems.items, cols);
    } else if (listItems.groups !== undefined) {
        groups = listItems.groups.map(g => ({
            title: g.title,
            items: groupArr(g.items, cols),
            onSeeAll: g.onSeeAll,
        }));
    } else {
        return panic("Must have either items or groups");
    }

    const paddingValue = asString(padding) as TilesPadding;

    let gap = pageMarginMedium;
    let bottomMargin = pageMarginMedium;

    if (paddingValue === TilesPadding.Tight) {
        gap = 1;
        bottomMargin = 1;
    }

    const ui = asUI(itemUI);

    const isFixed = textPositionValue === TextPosition.Overlay || !allowWrappingValue;
    const captionValue = loadedDefinedMapWithDefault(caption, "", asString);
    const emptyMessageValue = loadedDefinedMap(emptyMessage, asString);

    return (
        <List
            appFeatures={env.appEnvironment.appFeatures}
            eminenceFlags={env.appEnvironment.eminenceFlags}
            title={captionValue}
            hasUnevenRows={!isFixed}
            items={items}
            groups={groups}
            emptyMessage={emptyMessageValue}
            onSeeAllClick={onSeeAll !== undefined ? onSeeAll : undefined}
            style={{
                marginBottom: -bottomMargin,
            }}
            forceUpdate={fromData}
            topAlign={paddingValue === TilesPadding.Tight}>
            {(item, i, columnWidth) => {
                if (columnWidth === undefined) return null;

                const sidePadding = 2 * gap;
                const totalGapSize = (cols - 1) * gap;

                const colWidth = (columnWidth - totalGapSize - sidePadding) / cols;
                const e = env.with({ columnWidth: colWidth });
                return (
                    <Container gap={gap} bottomMargin={bottomMargin} cols={cols} key={i}>
                        {item.map((x, j) => {
                            const needsPush = i === "0-0" && j === 0 ? env.needsPush : false;
                            return ui.render(x, e.with({ needsPush }), j);
                        })}
                    </Container>
                );
            }}
        </List>
    );
};
